import React, { useRef, useState, useEffect } from 'react';
import utils from 'utils';
import { i18n } from 'utils/i18n';
import cn from 'classnames';
import useClickOutside from 'hooks/useClickOutside';
import Button from 'components/Button/Button';
import { formatKeys } from 'utils/accessibility/keys';

/**
 * CountryList Description:
 *
 * @param {object}          props
 * @param {state | boolean} props.showList
 * @param {state | boolean} props.setShowList
 * @param {Array}           props.selectedCountry
 * @param {Array}           props.countries
 * @param {function}        props.handleSelectCountry
 * @return {JSX} ...
 *
 */

const CountryList = ({
  showList,
  setShowList,
  countries,
  selectedCountry,
  handleSelectCountry,
  ariaLabeledBy,
  selectedCountryIndex,
  id,
  uniqueId,
}) => {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedId, setSelectedId] = useState(null);
  const highlightedOptionRef = useRef(null);
  const listRef = useRef(null);
  const togglerRef = useRef(null);
  const clickOutsideRef = useRef(null);

  const handleClickOutside = () => {
    setShowList(false);
  };

  useClickOutside(
    {
      container: clickOutsideRef,
      handler: handleClickOutside,
      listenerConditional: showList,
    },
    [showList]
  );

  const gcsIsoFlagsPath = utils.config.getGcsIsoFlagsPath();
  const getFlagUrl = (countryCode) => `${gcsIsoFlagsPath}${countryCode}.svg`;

  useEffect(() => {
    setHighlightedIndex((prevValue) => {
      if (selectedCountryIndex === prevValue) {
        // If no change, return the previous state. React bails out.
        return prevValue;
      }
      // Otherwise, return the new state
      return selectedCountryIndex;
    });
  }, [selectedCountryIndex]);
  /**
   * Scrolls scrollable dropdowns to the option by index
   *
   * @param index {Number}
   */
  const scrollToOptionList = (element) => {
    if (element && listRef.current) {
      return element;
    }
    return null;
  };

  const setHighlightedOptionRef = (element) => {
    highlightedOptionRef.current = element;
    if (element) {
      setSelectedId(element.id);
    }
  };

  useEffect(() => {
    const listNode = highlightedOptionRef.current;

    if (showList && selectedCountry && listNode) {
      const element = scrollToOptionList(listNode);

      if (selectedCountry && listRef.current) {
        listRef.current.scrollTop = element.offsetTop;
        listRef.current.focus();
        listRef.current.dispatchEvent(new CustomEvent('scroll'));
      }
    }
  }, [selectedCountry, selectedCountryIndex, highlightedOptionRef.current, showList]);

  /**
   * Scrolls scrollable dropdowns to the option by index
   *
   * @param index {Number}
   */
  const scrollToOption = (element) => {
    if (element && listRef.current) {
      listRef.current.scrollTop = element.offsetTop;
      listRef.current.dispatchEvent(new CustomEvent('scroll'));
    }
    return element;
  };

  /**
   * Highlights Option Item by Index
   *
   * @param index {Number}
   */
  const highlightOption = (index, shouldScrollToOption = false) => {
    setHighlightedIndex(index);
    if (shouldScrollToOption) {
      setTimeout(() => {
        if (highlightedOptionRef.current) {
          scrollToOption(highlightedOptionRef.current);
        }
      }, 0);
    }
  };

  /**
   * Highlights the previous sibling option of currently highlighted item
   */
  const highlightPrev = () => {
    const flatOptions = countries;
    if (!flatOptions.length) return;
    const prevIndex = highlightedIndex > 0 ? highlightedIndex - 1 : flatOptions.length - 1;
    highlightOption(prevIndex, true);
  };

  /**
   * Highlights the next sibling option of currently highlighted item
   */
  const highlightNext = () => {
    const flatOptions = countries;
    if (!flatOptions.length) return;
    const nextIndex = highlightedIndex < flatOptions.length - 1 ? highlightedIndex + 1 : 0;
    highlightOption(nextIndex, true);
  };

  const handleKeyDown = (e) => {
    const key = e.which || e.keyCode;
    const a11yKeys = formatKeys(e);
    const isA11yKeys = Object.values(a11yKeys).some(Boolean);
    const isAlphanumericKeys = /[a-zA-Z0-9-_ ]/.test(String.fromCharCode(key));
    const role = e.currentTarget.getAttribute('role');

    const isExpanded = showList;
    // If dropdown is closed while focus is on the toggler
    if (!isExpanded && (isA11yKeys || isAlphanumericKeys)) {
      if (
        !a11yKeys.tab &&
        !a11yKeys.shiftTab &&
        !a11yKeys.shift &&
        !a11yKeys.escape &&
        !a11yKeys.arrowLeft &&
        !a11yKeys.arrowRight
      ) {
        e.preventDefault();
        setShowList(!showList);

        if (a11yKeys.arrowUp) highlightPrev();
        else if (a11yKeys.arrowDown) highlightNext();
        else if (a11yKeys.home) highlightOption(0);
        else if (a11yKeys.end) highlightOption(countries.length - 1);
        else if (isAlphanumericKeys) {
          selectedCountryIndex && highlightOption(selectedCountryIndex, true);
        }
      }
    }

    // If dropdown expanded while focused
    // TODO: Add tab focus on tooltips if it exists
    if (isExpanded && role === 'listbox') {
      if (isA11yKeys || isAlphanumericKeys) e.preventDefault();
      const option = countries[highlightedIndex];

      switch (true) {
        case a11yKeys.arrowUp:
          // Highlight and scroll to previous item
          highlightPrev();
          break;
        case a11yKeys.arrowDown:
          // Highlight and scroll to next item
          highlightNext();
          break;
        case a11yKeys.home:
          // Highlight first item
          highlightOption(0, true);
          break;
        case a11yKeys.end:
          // Highlight last item
          highlightOption(countries.length - 1, true);
          break;
        case a11yKeys.escape:
        case a11yKeys.shiftTab:
        case a11yKeys.tab:
          // If escaping while focused on dropdown, refocus on Toggler before closing it
          e.stopPropagation();
          togglerRef.current.focus();
          setShowList(false);
          break;
        case a11yKeys.enter:
        case a11yKeys.space:
          if (option && !option.isSeparator) {
            // Focus on togglers
            togglerRef.current.focus();
            // Select current highlighted item and close Dropdown
            handleSelectCountry(option);
            setShowList(false);
          }
          break;
        default:
          if (isAlphanumericKeys) {
            e.stopPropagation();
            selectedCountryIndex && highlightOption(selectedCountryIndex, true);
          }
          break;
      }
    }
  };

  return (
    <div className='phone-number-field__country-box' ref={clickOutsideRef}>
      <Button
        link
        className='phone-number-field__btn'
        aria-haspopup='listbox'
        aria-expanded={showList ? 'true' : 'false'}
        id='country-list-phone-button'
        aria-controls={id}
        onClick={() => setShowList(!showList)}
        buttonRef={togglerRef}
        role='combobox'
        aria-label={i18n('wcag_change_country_code_selection', { 0: selectedCountry?.country_name })}
        onKeyDown={handleKeyDown}
      >
        {selectedCountry && (
          <img alt='' className='phone-number-field__flag' src={getFlagUrl(selectedCountry?.country_code)} />
        )}
      </Button>

      <ul
        ref={listRef}
        id={id}
        className={`phone-number-field__list ${showList ? 'open' : ''}`}
        role='listbox'
        aria-activedescendant={selectedId}
        aria-labelledby={`label_${ariaLabeledBy}`}
        onKeyDown={handleKeyDown}
      >
        {!!countries &&
          countries.map((country, index) => {
            const isHighlighted = highlightedIndex === index;

            return (
              <li
                className={cn('phone-number-field__list-item', {
                  highlight: isHighlighted,
                })}
                onClick={() => handleSelectCountry(country)}
                key={country?.country_code}
                id={`${country?.country_code}-${uniqueId}`}
                role='option'
                aria-selected={selectedCountry?.country_code === country.country_code ? 'true' : 'false'}
                ref={isHighlighted ? setHighlightedOptionRef : null}
                data-country-code={country.country_code}
                data-dial-code={country.country_content?.dialing_code}
              >
                <img className='phone-number-field__flag' src={getFlagUrl(country?.country_code)} />
                <span className='phone-number-field__dialing-code'>{country?.country_content?.dialing_code}</span>
                <span className='phone-number-field__country-name'>{country?.country_name}</span>
              </li>
            );
          })}
      </ul>
    </div>
  );
};

export default CountryList;
